// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Return / Debit Note form page (Stage 6P.8).
//
// Routes:
//   /erp/purchase-returns/new                            (create draft)
//   /erp/purchase-returns/:id                            (edit / confirm / post / cancel)
//   /erp/purchase-returns/from-inventory-ledger/:ledgerId
//                                                         (server-side
//                                                          snapshot from
//                                                          a posted GRN
//                                                          inventory row)
//   /erp/purchase-returns/from-purchase-bill/:purchaseBillId
//                                                         (server-side
//                                                          snapshot from
//                                                          a posted
//                                                          Purchase Bill)
//
// Drives the Stage 6P.8 Purchase Return + Debit Note pipeline:
//   * Save Draft        → POST  /api/erp/purchase-returns        (new)
//                       → PUT   /api/erp/purchase-returns/:id    (existing draft)
//   * Preview           → GET   /api/erp/purchase-returns/:id/preview
//   * Confirm           → POST  /api/erp/purchase-returns/:id/confirm
//   * Post Return       → POST  /api/erp/purchase-returns/:id/post
//   * Cancel Return     → POST  /api/erp/purchase-returns/:id/cancel
//
// Backend remains the source of truth for ALL math:
//   return_no / debit_note_no allocation, supplier resolution,
//   returnable-balance computation across GRN ↔ Purchase Bill,
//   ITC reversal posting, inventory OUTWARD movement, debit-note
//   voucher posting through the existing voucherPosting public
//   contract.
// Frontend mirrors for instant UX but never wins.
//
// Layout (brief §K2):
//   * Header card       (Return No, Date, Supplier, GSTIN, Return Type,
//                        Reason Code, Source PO/GRN/Bill, Debit Note,
//                        Supplier Credit Note, Remarks, status badges)
//   * Line grid         (AntD Table — common cols + GLASS / PROFILE
//                        conditional cols; recompute SQMT/SQFT/length
//                        and tax on every change; inline error if
//                        return_qty > returnable balance)
//   * Summary card      (Taxable, CGST, SGST, IGST, Total Tax,
//                        Round Off, Grand Total) + collapsible
//                        Inventory / Accounting / ITC preview blocks
//   * Footer (sticky)   (Save Draft · Preview · Confirm · Post · Cancel · Back)
//
// Mirrors the structure of SupplierPaymentFormPage so the visual
// language stays consistent across 6P.6 / 6P.7 / 6P.8.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Collapse,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Result,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  ReloadOutlined,
  SaveOutlined,
  SendOutlined,
  StopOutlined,
  UndoOutlined,
  WarningOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { purchaseReturnApi } from './purchaseReturnApi';
import {
  RETURN_TYPES,
  REASON_CODES,
  ITEM_TYPES,
  buildPurchaseReturnPayload,
  calculateLineTax,
  calculateReturnSqft,
  calculateReturnSqmt,
  calculateReturnTotalLength,
  canCancelPurchaseReturn,
  canConfirmPurchaseReturn,
  canEditPurchaseReturn,
  canPostPurchaseReturn,
  postingStatusColor,
  postingStatusLabel,
  returnStatusColor,
  returnStatusLabel,
  reasonCodeLabel,
  returnTypeLabel,
  validatePurchaseReturn
} from './purchaseReturnHelpers';

import client from '../../../api/client';
import useAuth from '../../../hooks/useAuth';
import { buildDropdownStateProps } from '../common/dropdownLoaderState.js';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric formatter — preview numbers arrive as JS numbers
// from the API. Zero collapses to a dim '—' so empty cells stay
// scannable in the per-row summary columns.
// ─────────────────────────────────────────────────────────────────
function fmtMoney(v, decimals = 2) {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '—';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

function fmtMoneyAlways(v, decimals = 2) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0.00';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

function fmtNumber(v, decimals = 3) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

function fmtDate(v) {
  if (!v) return '—';
  const s = String(v);
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return s;
}

function fmtDateTime(v) {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isFinite(d.getTime())) {
      return d.toLocaleString(undefined, {
        year:   'numeric',
        month:  '2-digit',
        day:    '2-digit',
        hour:   '2-digit',
        minute: '2-digit'
      });
    }
  } catch (_) { /* fall through */ }
  return String(v);
}

function toDayjsOrNull(v) {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
}

function isoOrNull(v) {
  if (!v) return null;
  if (typeof v === 'string') return v;
  if (v && typeof v.format === 'function') return v.format('YYYY-MM-DD');
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return v.toISOString().slice(0, 10);
  }
  return null;
}

function toNumber(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function round2(v) {
  const n = toNumber(v);
  if (n === 0) return 0;
  return Math.round(n * 100) / 100;
}

function round4(v) {
  const n = toNumber(v);
  if (n === 0) return 0;
  return Math.round(n * 10000) / 10000;
}


// ─────────────────────────────────────────────────────────────────
// Item-type conditional column gates. Glass needs thickness + W + H,
// profile / upvc / aluminium need length + length UOM. Everything
// else hides those columns to keep the grid scannable.
// ─────────────────────────────────────────────────────────────────
const GLASS_TYPES   = new Set(['GLASS']);
const LENGTH_TYPES  = new Set(['PROFILE', 'UPVC', 'ALUMINIUM']);

function isGlass(type)  { return GLASS_TYPES.has(String(type || '').toUpperCase()); }
function isLength(type) { return LENGTH_TYPES.has(String(type || '').toUpperCase()); }


// ─────────────────────────────────────────────────────────────────
// Inline tax math — mirrors backend purchaseReturnCalculation. The
// helpers file owns the canonical numerics; this fallback exists so a
// dropped helper doesn't blank out the grid mid-edit.
// ─────────────────────────────────────────────────────────────────
function localCalculateLineTax(line) {
  const qty   = toNumber(line.returnQty);
  const rate  = toNumber(line.rate);
  const gross = round2(qty * rate);

  const discPct = toNumber(line.discountPercent);
  const discAmt = round2((gross * discPct) / 100);

  const taxable = round2(gross - discAmt);
  const gstRate = toNumber(line.gstRate);
  const totalGst = round2((taxable * gstRate) / 100);

  // Intra-state vs inter-state split is set by the supplier, not the
  // line. The form picks the split mode from `interState` flag on the
  // header; default to half-split when unknown so totals stay sane.
  const isInter = !!line.interState;
  const cgst = isInter ? 0 : round2(totalGst / 2);
  const sgst = isInter ? 0 : round2(totalGst - cgst);
  const igst = isInter ? totalGst : 0;
  const lineTotal = round2(taxable + cgst + sgst + igst);

  return {
    grossAmount:    gross,
    discountAmount: discAmt,
    taxableAmount:  taxable,
    cgstAmount:     cgst,
    sgstAmount:     sgst,
    igstAmount:     igst,
    lineTotal
  };
}

function recomputeLineMath(line, headerInterState) {
  const next = { ...line, interState: !!headerInterState };

  // SQMT / SQFT for glass — width + height in mm, qty as count.
  if (isGlass(next.itemType)) {
    try {
      const sqmt = typeof calculateReturnSqmt === 'function'
        ? calculateReturnSqmt({ widthMm: next.widthMm, heightMm: next.heightMm, returnQty: next.returnQty })
        : null;
      const sqft = typeof calculateReturnSqft === 'function'
        ? calculateReturnSqft({ widthMm: next.widthMm, heightMm: next.heightMm, returnQty: next.returnQty })
        : null;
      next.returnSqmt = sqmt !== null && sqmt !== undefined ? round4(sqmt)
        : round4(((toNumber(next.widthMm) * toNumber(next.heightMm)) / 1_000_000) * toNumber(next.returnQty));
      next.returnSqft = sqft !== null && sqft !== undefined ? round4(sqft)
        : round4(next.returnSqmt * 10.764);
    } catch (_) {
      next.returnSqmt = round4(((toNumber(next.widthMm) * toNumber(next.heightMm)) / 1_000_000) * toNumber(next.returnQty));
      next.returnSqft = round4(next.returnSqmt * 10.764);
    }
  } else {
    next.returnSqmt = 0;
    next.returnSqft = 0;
  }

  // Total length for profile / upvc / aluminium — length * qty.
  if (isLength(next.itemType)) {
    try {
      const total = typeof calculateReturnTotalLength === 'function'
        ? calculateReturnTotalLength({ lengthValue: next.lengthValue, returnQty: next.returnQty })
        : null;
      next.returnTotalLength = total !== null && total !== undefined
        ? round4(total)
        : round4(toNumber(next.lengthValue) * toNumber(next.returnQty));
    } catch (_) {
      next.returnTotalLength = round4(toNumber(next.lengthValue) * toNumber(next.returnQty));
    }
  } else {
    next.returnTotalLength = 0;
  }

  // Tax math — prefer the canonical helper when present.
  let tax;
  try {
    tax = typeof calculateLineTax === 'function'
      ? calculateLineTax({ ...next, interState: !!headerInterState })
      : localCalculateLineTax({ ...next, interState: !!headerInterState });
  } catch (_) {
    tax = localCalculateLineTax({ ...next, interState: !!headerInterState });
  }

  next.grossAmount    = round2(tax.grossAmount);
  next.discountAmount = round2(tax.discountAmount);
  next.taxableAmount  = round2(tax.taxableAmount);
  next.cgstAmount     = round2(tax.cgstAmount);
  next.sgstAmount     = round2(tax.sgstAmount);
  next.igstAmount     = round2(tax.igstAmount);
  next.lineTotal      = round2(tax.lineTotal);

  return next;
}


// ─────────────────────────────────────────────────────────────────
// Header totals — sums + half-up rounding. Round-off is taken from
// the header. Grand total is taxable + total tax + round-off (rupee
// precision).
// ─────────────────────────────────────────────────────────────────
function summariseLines(lines, roundOff) {
  let taxable = 0, cgst = 0, sgst = 0, igst = 0;
  for (const l of (Array.isArray(lines) ? lines : [])) {
    taxable += toNumber(l.taxableAmount);
    cgst    += toNumber(l.cgstAmount);
    sgst    += toNumber(l.sgstAmount);
    igst    += toNumber(l.igstAmount);
  }
  taxable = round2(taxable);
  cgst    = round2(cgst);
  sgst    = round2(sgst);
  igst    = round2(igst);
  const totalTax  = round2(cgst + sgst + igst);
  const ro        = round2(roundOff);
  const grandTotal = round2(taxable + totalTax + ro);
  return { taxableAmount: taxable, cgstAmount: cgst, sgstAmount: sgst, igstAmount: igst, totalTax, roundOff: ro, grandTotal };
}


// ─────────────────────────────────────────────────────────────────
// Main page component
// ─────────────────────────────────────────────────────────────────
export default function PurchaseReturnFormPage(props) {
  const params = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();

  // Mode discovery — props.mode wins, fallback inferred from URL params.
  const mode = useMemo(() => {
    if (props && props.mode) return props.mode;
    if (params.ledgerId) return 'from-inventory-ledger';
    if (params.purchaseBillId) return 'from-purchase-bill';
    if (params.id && params.id !== 'new') return 'edit';
    return 'new';
  }, [props, params]);

  const routeId         = params.id || null;
  const ledgerId        = params.ledgerId || null;
  const purchaseBillId  = params.purchaseBillId || null;
  const isCreate        = mode === 'new' || mode === 'from-inventory-ledger' || mode === 'from-purchase-bill';

  // RBAC mirror (server is authority; this hides CTAs only).
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canCreatePerm  = hasPerm('erp.purchase.returns.create');
  const canUpdatePerm  = hasPerm('erp.purchase.returns.update');
  const canConfirmPerm = hasPerm('erp.purchase.returns.confirm');
  const canPostPerm    = hasPerm('erp.purchase.returns.post');
  const canCancelPerm  = hasPerm('erp.purchase.returns.cancel');

  const [form] = Form.useForm();

  // ── State ─────────────────────────────────────────────────────
  const [record,     setRecord]     = useState(null);     // loaded purchase return
  const [preview,    setPreview]    = useState(null);     // server preview
  const [loading,    setLoading]    = useState(!isCreate || mode === 'from-inventory-ledger' || mode === 'from-purchase-bill');
  const [loadError,  setLoadError]  = useState(null);

  const [saving,     setSaving]     = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [posting,    setPosting]    = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const [postResult, setPostResult] = useState(null);
  const [postError,  setPostError]  = useState(null);

  // Lines live outside Form so per-cell edits don't trigger a full
  // form re-render — keeps qty / rate edits snappy on long grids.
  const [lines, setLines] = useState([]);

  // Lookups. `supplierError` drives the AntD Select notFoundContent
  // via the shared `buildDropdownStateProps` helper so a failed loader
  // shows "Failed to load — try refresh" instead of a silent empty
  // dropdown (Stage 6P-MED-FIX F-FD-7).
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [supplierError,   setSupplierError]   = useState(null);

  // Watched header fields driving conditional behaviour.
  const supplierId      = Form.useWatch('supplierId', form);
  const supplierGstin   = Form.useWatch('supplierGstin', form);
  const headerRoundOff  = Form.useWatch('roundOff', form);

  // Header inter-state flag — defaults to false; preview / record can
  // override. Used by the in-grid tax recomputation.
  const interState = useMemo(() => {
    if (record && (record.interState !== undefined && record.interState !== null)) {
      return !!record.interState;
    }
    if (preview && preview.interState !== undefined) return !!preview.interState;
    return false;
  }, [record, preview]);


  // ── Hydrate from record / draft helper ────────────────────────
  const hydrateFromRecord = useCallback((data) => {
    if (!data) return;
    setRecord(data);
    form.setFieldsValue({
      returnDate:             toDayjsOrNull(data.returnDate || data.return_date),
      returnType:             data.returnType || data.return_type || 'MATERIAL_RETURN',
      reasonCode:             data.reasonCode || data.reason_code || 'QUALITY_REJECTED',
      supplierId:             data.supplierId || data.supplier_id || null,
      supplierName:           data.supplierName || data.supplier_name || '',
      supplierGstin:          data.supplierGstin || data.supplier_gstin || '',
      purchaseOrderId:        data.purchaseOrderId || data.purchase_order_id || null,
      poNo:                   data.poNo || data.po_no || '',
      goodsReceiptId:         data.goodsReceiptId || data.goods_receipt_id || null,
      grnNo:                  data.grnNo || data.grn_no || '',
      purchaseBillId:         data.purchaseBillId || data.purchase_bill_id || null,
      purchaseBillNo:         data.purchaseBillNo || data.purchase_bill_no || '',
      debitNoteNo:            data.debitNoteNo || data.debit_note_no || '',
      debitNoteDate:          toDayjsOrNull(data.debitNoteDate || data.debit_note_date),
      supplierCreditNoteNo:   data.supplierCreditNoteNo || data.supplier_credit_note_no || '',
      supplierCreditNoteDate: toDayjsOrNull(data.supplierCreditNoteDate || data.supplier_credit_note_date),
      roundOff:               Number(data.roundOff || data.round_off || 0) || 0,
      remarks:                data.remarks || ''
    });
    const linesIn = Array.isArray(data.lines) ? data.lines : [];
    setLines(
      linesIn.map((l, idx) => ({
        key:                       l.id || `existing-${idx}`,
        id:                        l.id || null,
        lineNo:                    Number(l.lineNo || l.line_no || (idx + 1)),
        inventoryStockLedgerId:    l.inventoryStockLedgerId || l.inventory_stock_ledger_id || null,
        goodsReceiptLineId:        l.goodsReceiptLineId || l.goods_receipt_line_id || null,
        purchaseBillLineId:        l.purchaseBillLineId || l.purchase_bill_line_id || null,
        purchaseOrderLineId:       l.purchaseOrderLineId || l.purchase_order_line_id || null,
        itemId:                    l.itemId || l.item_id || null,
        itemType:                  (l.itemType || l.item_type || 'OTHER'),
        itemName:                  l.itemName || l.item_name || '',
        itemDesc:                  l.itemDesc || l.item_desc || '',
        stockName:                 l.stockName || l.stock_name || '',
        itemDescription:           l.itemDescription || l.item_description || '',
        thicknessMm:               Number(l.thicknessMm || l.thickness_mm || 0) || null,
        widthMm:                   Number(l.widthMm || l.width_mm || 0) || null,
        heightMm:                  Number(l.heightMm || l.height_mm || 0) || null,
        returnQty:                 Number(l.returnQty || l.return_qty || 0) || 0,
        returnSqmt:                Number(l.returnSqmt || l.return_sqmt || 0) || 0,
        returnSqft:                Number(l.returnSqft || l.return_sqft || 0) || 0,
        lengthValue:               Number(l.lengthValue || l.length_value || 0) || null,
        lengthUom:                 l.lengthUom || l.length_uom || '',
        returnTotalLength:         Number(l.returnTotalLength || l.return_total_length || 0) || 0,
        uom:                       l.uom || '',
        rate:                      Number(l.rate || 0) || 0,
        grossAmount:               Number(l.grossAmount || l.gross_amount || 0) || 0,
        discountPercent:           Number(l.discountPercent || l.discount_percent || 0) || 0,
        discountAmount:            Number(l.discountAmount || l.discount_amount || 0) || 0,
        taxableAmount:             Number(l.taxableAmount || l.taxable_amount || 0) || 0,
        gstRate:                   Number(l.gstRate || l.gst_rate || 0) || 0,
        cgstAmount:                Number(l.cgstAmount || l.cgst_amount || 0) || 0,
        sgstAmount:                Number(l.sgstAmount || l.sgst_amount || 0) || 0,
        igstAmount:                Number(l.igstAmount || l.igst_amount || 0) || 0,
        lineTotal:                 Number(l.lineTotal || l.line_total || 0) || 0,
        alreadyReturnedQty:        Number(l.alreadyReturnedQty || l.already_returned_qty || 0) || 0,
        returnBalanceQty:          Number(l.returnBalanceQty || l.return_balance_qty || 0) || 0,
        alreadyReturnedSqmt:       Number(l.alreadyReturnedSqmt || l.already_returned_sqmt || 0) || 0,
        returnBalanceSqmt:         Number(l.returnBalanceSqmt || l.return_balance_sqmt || 0) || 0,
        alreadyReturnedSqft:       Number(l.alreadyReturnedSqft || l.already_returned_sqft || 0) || 0,
        returnBalanceSqft:         Number(l.returnBalanceSqft || l.return_balance_sqft || 0) || 0,
        alreadyReturnedTotalLength:
                                   Number(l.alreadyReturnedTotalLength || l.already_returned_total_length || 0) || 0,
        returnBalanceTotalLength:  Number(l.returnBalanceTotalLength || l.return_balance_total_length || 0) || 0,
        remarks:                   l.remarks || ''
      }))
    );
  }, [form]);


  // ── Initial load ──────────────────────────────────────────────
  const loadRecord = useCallback(async () => {
    if (mode === 'new') {
      // Apply create-mode defaults.
      form.setFieldsValue({
        returnDate: dayjs(),
        returnType: 'MATERIAL_RETURN',
        reasonCode: 'QUALITY_REJECTED',
        roundOff:   0
      });
      setLines([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      let data;
      if (mode === 'edit') {
        data = await purchaseReturnApi.get(routeId);
      } else if (mode === 'from-inventory-ledger') {
        data = await purchaseReturnApi.createFromInventoryLedger(ledgerId, {});
      } else if (mode === 'from-purchase-bill') {
        data = await purchaseReturnApi.createFromPurchaseBill(purchaseBillId, {});
      }
      hydrateFromRecord(data || null);

      // Server-side "draft from X" lands us at an id-bearing draft; if
      // we landed here from from-X mode, redirect to the canonical
      // /:id form route so deep-links and refresh work.
      if ((mode === 'from-inventory-ledger' || mode === 'from-purchase-bill') &&
          data && data.id) {
        navigate(`/erp/purchase-returns/${data.id}`, { replace: true });
      }
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load purchase return.';
      setLoadError(msg);
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [mode, routeId, ledgerId, purchaseBillId, form, hydrateFromRecord, navigate]);

  useEffect(() => { loadRecord(); }, [loadRecord]);


  // ── Supplier dropdown loader ──────────────────────────────────
  const loadSuppliers = useCallback(async (q = '') => {
    setSupplierLoading(true);
    setSupplierError(null);
    try {
      const resp = await client.get('/api/erp/accounts/parties/suppliers', {
        params: { ...(q ? { q } : {}), limit: 100 }
      });
      const rows = resp && resp.data && resp.data.data !== undefined
        ? resp.data.data
        : (resp && resp.data) || [];
      setSupplierOptions(Array.isArray(rows) ? rows : []);
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load suppliers.';
      setSupplierError(msg);
      setSupplierOptions([]);
    } finally {
      setSupplierLoading(false);
    }
  }, []);

  useEffect(() => { loadSuppliers(''); }, [loadSuppliers]);

  // Auto-fill GSTIN + supplier name when the supplier select changes.
  useEffect(() => {
    if (!supplierId) return;
    const match = supplierOptions.find((s) =>
      (s.id || s.supplierId) === supplierId
    );
    if (match) {
      const g = match.gstin || match.supplierGstin || match.gstNumber || '';
      const n = match.supplierName || match.name || '';
      const patch = {};
      if (g && !supplierGstin) patch.supplierGstin = g;
      if (n) patch.supplierName = n;
      if (Object.keys(patch).length > 0) form.setFieldsValue(patch);
    }
  }, [supplierId, supplierOptions, supplierGstin, form]);


  // ── Per-line mutation ─────────────────────────────────────────
  const updateLine = useCallback((idx, patch) => {
    setLines((prev) => {
      const next = prev.slice();
      const merged = { ...next[idx], ...patch };
      next[idx] = recomputeLineMath(merged, interState);
      return next;
    });
  }, [interState]);

  const removeLine = useCallback((idx) => {
    setLines((prev) => {
      const next = prev.slice();
      next.splice(idx, 1);
      return next.map((l, i) => ({ ...l, lineNo: i + 1 }));
    });
  }, []);

  const addLine = useCallback(() => {
    setLines((prev) => {
      const blank = {
        key:                       `new-${Date.now()}-${prev.length}`,
        id:                        null,
        lineNo:                    prev.length + 1,
        inventoryStockLedgerId:    null,
        goodsReceiptLineId:        null,
        purchaseBillLineId:        null,
        purchaseOrderLineId:       null,
        itemId:                    null,
        itemType:                  'OTHER',
        itemName:                  '',
        itemDesc:                  '',
        stockName:                 '',
        itemDescription:           '',
        thicknessMm:               null,
        widthMm:                   null,
        heightMm:                  null,
        returnQty:                 0,
        returnSqmt:                0,
        returnSqft:                0,
        lengthValue:               null,
        lengthUom:                 '',
        returnTotalLength:         0,
        uom:                       '',
        rate:                      0,
        grossAmount:               0,
        discountPercent:           0,
        discountAmount:            0,
        taxableAmount:             0,
        gstRate:                   0,
        cgstAmount:                0,
        sgstAmount:                0,
        igstAmount:                0,
        lineTotal:                 0,
        alreadyReturnedQty:        0,
        returnBalanceQty:          0,
        alreadyReturnedSqmt:       0,
        returnBalanceSqmt:         0,
        alreadyReturnedSqft:       0,
        returnBalanceSqft:         0,
        alreadyReturnedTotalLength: 0,
        returnBalanceTotalLength:  0,
        remarks:                   ''
      };
      return prev.concat([blank]);
    });
  }, []);


  // ── Header totals (live) ──────────────────────────────────────
  const headerTotals = useMemo(
    () => summariseLines(lines, headerRoundOff || 0),
    [lines, headerRoundOff]
  );


  // ── Build payload ─────────────────────────────────────────────
  const buildPayload = useCallback(async () => {
    const values = await form.validateFields();
    const linesPayload = lines.map((l) => ({
      id:                            l.id || undefined,
      line_no:                       l.lineNo,
      inventory_stock_ledger_id:     l.inventoryStockLedgerId || null,
      goods_receipt_line_id:         l.goodsReceiptLineId || null,
      purchase_bill_line_id:         l.purchaseBillLineId || null,
      purchase_order_line_id:        l.purchaseOrderLineId || null,
      item_id:                       l.itemId || null,
      item_type:                     l.itemType || 'OTHER',
      item_name:                     l.itemName || '',
      item_desc:                     l.itemDesc || null,
      stock_name:                    l.stockName || null,
      item_description:              l.itemDescription || null,
      thickness_mm:                  l.thicknessMm || null,
      width_mm:                      l.widthMm || null,
      height_mm:                     l.heightMm || null,
      return_qty:                    toNumber(l.returnQty),
      return_sqmt:                   toNumber(l.returnSqmt),
      return_sqft:                   toNumber(l.returnSqft),
      length_value:                  l.lengthValue || null,
      length_uom:                    l.lengthUom || null,
      return_total_length:           toNumber(l.returnTotalLength),
      uom:                           l.uom || '',
      rate:                          toNumber(l.rate),
      gross_amount:                  toNumber(l.grossAmount),
      discount_percent:              toNumber(l.discountPercent),
      discount_amount:               toNumber(l.discountAmount),
      taxable_amount:                toNumber(l.taxableAmount),
      gst_rate:                      toNumber(l.gstRate),
      cgst_amount:                   toNumber(l.cgstAmount),
      sgst_amount:                   toNumber(l.sgstAmount),
      igst_amount:                   toNumber(l.igstAmount),
      line_total:                    toNumber(l.lineTotal),
      remarks:                       l.remarks || null
    }));

    const formValues = {
      returnDate:             isoOrNull(values.returnDate),
      returnType:             values.returnType,
      reasonCode:             values.reasonCode,
      supplierId:             values.supplierId,
      supplierName: (supplierOptions.find(
        (s) => (s.id || s.supplierId) === values.supplierId
      ) || {}).supplierName ||
        (supplierOptions.find(
          (s) => (s.id || s.supplierId) === values.supplierId
        ) || {}).name ||
        values.supplierName ||
        (record && (record.supplierName || record.supplier_name)) || '',
      supplierGstin:          values.supplierGstin || '',
      purchaseOrderId:        values.purchaseOrderId || (record && record.purchaseOrderId) || null,
      poNo:                   values.poNo || (record && record.poNo) || '',
      goodsReceiptId:         values.goodsReceiptId || (record && record.goodsReceiptId) || null,
      grnNo:                  values.grnNo || (record && record.grnNo) || '',
      purchaseBillId:         values.purchaseBillId || (record && record.purchaseBillId) || null,
      purchaseBillNo:         values.purchaseBillNo || (record && record.purchaseBillNo) || '',
      debitNoteNo:            values.debitNoteNo || '',
      debitNoteDate:          isoOrNull(values.debitNoteDate),
      supplierCreditNoteNo:   values.supplierCreditNoteNo || '',
      supplierCreditNoteDate: isoOrNull(values.supplierCreditNoteDate),
      roundOff:               toNumber(values.roundOff),
      remarks:                values.remarks || '',
      lines:                  linesPayload
    };

    if (typeof buildPurchaseReturnPayload === 'function') {
      try {
        return buildPurchaseReturnPayload(formValues);
      } catch (_e) {
        // Helpers may throw on partial data — fall through to the
        // hand-rolled snake_case shape below so the user can still
        // hit Save without us blanking the request.
      }
    }

    // Fallback shape — snake_case mirrors backend Joi validator.
    return {
      return_date:               formValues.returnDate || null,
      return_type:               formValues.returnType,
      reason_code:               formValues.reasonCode,
      supplier_id:               formValues.supplierId || null,
      supplier_name:             formValues.supplierName,
      supplier_gstin:            formValues.supplierGstin || null,
      purchase_order_id:         formValues.purchaseOrderId || null,
      po_no:                     formValues.poNo || null,
      goods_receipt_id:          formValues.goodsReceiptId || null,
      grn_no:                    formValues.grnNo || null,
      purchase_bill_id:          formValues.purchaseBillId || null,
      purchase_bill_no:          formValues.purchaseBillNo || null,
      debit_note_no:             formValues.debitNoteNo || null,
      debit_note_date:           formValues.debitNoteDate || null,
      supplier_credit_note_no:   formValues.supplierCreditNoteNo || null,
      supplier_credit_note_date: formValues.supplierCreditNoteDate || null,
      round_off:                 formValues.roundOff || 0,
      remarks:                   formValues.remarks || null,
      lines:                     formValues.lines
    };
  }, [form, lines, supplierOptions, record]);


  // ── Frontend validation summary (UI mirror) ───────────────────
  const validationErrors = useMemo(() => {
    const errs = [];
    if (lines.length === 0) {
      errs.push('At least one return line is required.');
    }
    for (const l of lines) {
      if (!l.itemName) {
        errs.push(`Line ${l.lineNo}: item name is required.`);
      }
      if (!(toNumber(l.returnQty) > 0)) {
        errs.push(`Line ${l.lineNo}: return qty must be greater than zero.`);
      }
      if (toNumber(l.discountPercent) < 0 || toNumber(l.discountPercent) > 100) {
        errs.push(`Line ${l.lineNo}: discount % must be between 0 and 100.`);
      }
      if (toNumber(l.gstRate) < 0 || toNumber(l.gstRate) > 28) {
        errs.push(`Line ${l.lineNo}: GST rate must be between 0 and 28.`);
      }
      // Returnable balance — only validated when the line carries a
      // backend-supplied balance (i.e. linked to a GRN / Bill line).
      const balance = toNumber(l.returnBalanceQty);
      if (balance > 0 && toNumber(l.returnQty) - balance > 0.001) {
        errs.push(
          `Line ${l.lineNo}: return qty (${fmtNumber(l.returnQty)}) exceeds returnable balance (${fmtNumber(balance)}).`
        );
      }
    }
    // Delegate to helper validator when present for cross-line / header
    // invariants the UI shouldn't duplicate.
    if (typeof validatePurchaseReturn === 'function') {
      try {
        const v = validatePurchaseReturn({
          lines,
          taxableAmount: headerTotals.taxableAmount,
          cgstAmount:    headerTotals.cgstAmount,
          sgstAmount:    headerTotals.sgstAmount,
          igstAmount:    headerTotals.igstAmount,
          grandTotal:    headerTotals.grandTotal
        });
        if (v && !v.ok && Array.isArray(v.errors)) {
          for (const e of v.errors) errs.push((e && e.message) || 'Validation issue.');
        }
      } catch (_e) { /* helper outage shouldn't block save */ }
    }
    return Array.from(new Set(errs));
  }, [lines, headerTotals]);


  // ── Save / Update draft ───────────────────────────────────────
  const handleSaveDraft = useCallback(async () => {
    setSaving(true);
    setPostError(null);
    try {
      const payload = await buildPayload();
      let saved;
      if (isCreate || !record || !record.id) {
        saved = await purchaseReturnApi.create(payload);
        message.success('Draft created.');
        if (saved && saved.id) {
          navigate(`/erp/purchase-returns/${saved.id}`, { replace: true });
        }
      } else {
        saved = await purchaseReturnApi.update(record.id, payload);
        message.success('Draft updated.');
      }
      if (saved) hydrateFromRecord(saved);
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.errorFields ? 'Please fix the highlighted fields.' : null)
        || (e && e.message)
        || 'Failed to save draft.';
      message.error(msg);
      setPostError(msg);
    } finally {
      setSaving(false);
    }
  }, [buildPayload, isCreate, record, navigate, hydrateFromRecord]);


  // ── Preview ───────────────────────────────────────────────────
  const handlePreview = useCallback(async () => {
    if (!record || !record.id) {
      message.warning('Save the draft before requesting a preview.');
      return;
    }
    setPreviewing(true);
    setPostError(null);
    try {
      const data = await purchaseReturnApi.preview(record.id);
      setPreview(data || null);
      if (data && Array.isArray(data.blockers) && data.blockers.length > 0) {
        message.warning(`Preview returned ${data.blockers.length} blocker${data.blockers.length === 1 ? '' : 's'}.`);
      } else {
        message.success('Preview ready.');
      }
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to load preview.';
      message.error(msg);
      setPostError(msg);
    } finally {
      setPreviewing(false);
    }
  }, [record]);


  // ── Confirm (DRAFT → CONFIRMED) ───────────────────────────────
  const handleConfirm = useCallback(async () => {
    if (!record || !record.id) {
      message.warning('Save the draft before confirming.');
      return;
    }
    setConfirming(true);
    setPostError(null);
    try {
      const updated = await purchaseReturnApi.confirm(record.id, {});
      message.success('Return confirmed.');
      hydrateFromRecord(updated || record);
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to confirm return.';
      message.error(msg);
      setPostError(msg);
    } finally {
      setConfirming(false);
    }
  }, [record, hydrateFromRecord]);


  // ── Post return ───────────────────────────────────────────────
  const handlePost = useCallback(async () => {
    if (!record || !record.id) {
      message.warning('Save the draft before posting.');
      return;
    }
    setPosting(true);
    setPostError(null);
    try {
      const result = await purchaseReturnApi.post(record.id, {});

      const status =
        (result && (result.status || result.postingStatus)) ||
        'POSTED';

      const voucherNo =
        (result && (result.accountingVoucherNo || result.voucher_no || result.voucherNo)) ||
        null;

      const postedAt =
        (result && (result.postedAt || result.posted_at)) ||
        null;

      const failureReason =
        (result && (result.failureReason || result.failure_reason)) ||
        null;

      const success = !failureReason && String(status).toUpperCase() === 'POSTED';

      setPostResult({
        success,
        status,
        voucherNo,
        postedAt,
        errorMessage: failureReason || null
      });

      if (success) {
        message.success(`Posted → ${voucherNo || 'debit note voucher'}.`);
      } else {
        message.warning(
          failureReason
            ? `Posting failed: ${failureReason}`
            : 'Posting did not complete successfully.'
        );
      }
      await loadRecord();
    } catch (e) {
      const msg = (e && e.response && e.response.data &&
                    (e.response.data.message || e.response.data.code))
        || (e && e.message)
        || 'Failed to post the return.';
      setPostError(msg);
      message.error(msg);
    } finally {
      setPosting(false);
    }
  }, [record, loadRecord]);


  // ── Cancel return ─────────────────────────────────────────────
  const handleCancel = useCallback(() => {
    if (!record || !record.id) return;
    let reason = '';
    Modal.confirm({
      title: 'Cancel this purchase return?',
      icon:  <ExclamationCircleOutlined />,
      content: (
        <div>
          <Paragraph>
            Cancelling reverses the debit-note voucher, the inventory
            OUTWARD movement and the ITC reversal. This cannot be undone.
          </Paragraph>
          <Input.TextArea
            placeholder="Reason (optional)…"
            rows={3}
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText:     'Cancel Return',
      okType:     'danger',
      cancelText: 'Back',
      async onOk() {
        setCancelling(true);
        try {
          await purchaseReturnApi.cancel(record.id, reason || undefined);
          message.success('Return cancelled.');
          await loadRecord();
          setPreview(null);
          setPostResult(null);
        } catch (e) {
          const msg = (e && e.response && e.response.data &&
                        (e.response.data.message || e.response.data.code))
            || (e && e.message)
            || 'Failed to cancel return.';
          message.error(msg);
        } finally {
          setCancelling(false);
        }
      }
    });
  }, [record, loadRecord]);


  // ── Derived UI gates ──────────────────────────────────────────
  const status        = record ? (record.status || record.return_status || null) : null;
  const postingStatus = record ? (record.postingStatus || record.posting_status || null) : null;
  const returnNo      = record ? (record.returnNo || record.return_no || null) : null;
  const voucherNo     = record ? (record.accountingVoucherNo || record.accounting_voucher_no || record.voucherNo || null) : null;

  const editable = isCreate || (
    (typeof canEditPurchaseReturn === 'function')
      ? canEditPurchaseReturn(status)
      : (status === 'DRAFT' || status === 'POSTING_FAILED')
  );

  const isEditable = editable && (isCreate ? canCreatePerm : canUpdatePerm);

  const canConfirmBtn = !!record && !!record.id &&
    ((typeof canConfirmPurchaseReturn === 'function')
      ? canConfirmPurchaseReturn(status)
      : status === 'DRAFT') &&
    canConfirmPerm;

  const canPostBtn = !!record && !!record.id &&
    ((typeof canPostPurchaseReturn === 'function')
      ? canPostPurchaseReturn(status)
      : (status === 'CONFIRMED' || status === 'POSTING_FAILED')) &&
    validationErrors.length === 0 &&
    canPostPerm;

  const canCancelBtn = !!record && !!record.id &&
    ((typeof canCancelPurchaseReturn === 'function')
      ? canCancelPurchaseReturn(status)
      : (status === 'POSTED' || status === 'POSTING_FAILED')) &&
    canCancelPerm;


  // ── Line grid columns ─────────────────────────────────────────
  const lineColumns = useMemo(() => {
    const cols = [
      {
        title:  '#',
        width:  40,
        align:  'center',
        fixed:  'left',
        render: (_, __, idx) => idx + 1
      },
      {
        title:     'Item Type',
        dataIndex: 'itemType',
        width:     130,
        fixed:     'left',
        render:    (v, _row, idx) => (
          <Select
            size="small"
            value={v || 'OTHER'}
            disabled={!isEditable}
            style={{ width: '100%' }}
            options={ITEM_TYPES_LIST.map((t) => ({ value: t, label: t }))}
            onChange={(val) => updateLine(idx, { itemType: val })}
          />
        )
      },
      {
        title:     'Item / Stock',
        dataIndex: 'itemName',
        width:     200,
        fixed:     'left',
        render:    (v, row, idx) => (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Input
              size="small"
              value={v || ''}
              disabled={!isEditable}
              placeholder="Item name"
              onChange={(e) => updateLine(idx, { itemName: e.target.value })}
            />
            {row.stockName
              ? <Text type="secondary" style={{ fontSize: 11 }}>{row.stockName}</Text>
              : null}
          </Space>
        )
      }
    ];

    // Glass-only conditional columns — surfaced when ANY line is glass
    // so the operator can compare side-by-side.
    const anyGlass  = lines.some((l) => isGlass(l.itemType));
    const anyLength = lines.some((l) => isLength(l.itemType));

    if (anyGlass) {
      cols.push(
        {
          title:     'Thickness',
          dataIndex: 'thicknessMm',
          width:     90,
          align:     'right',
          render:    (v, row, idx) => isGlass(row.itemType) ? (
            <InputNumber
              size="small"
              min={0}
              step={0.1}
              precision={2}
              value={v}
              disabled={!isEditable}
              style={{ width: '100%' }}
              onChange={(val) => updateLine(idx, { thicknessMm: Number(val) || null })}
            />
          ) : '—'
        },
        {
          title:     'W (mm)',
          dataIndex: 'widthMm',
          width:     90,
          align:     'right',
          render:    (v, row, idx) => isGlass(row.itemType) ? (
            <InputNumber
              size="small"
              min={0}
              step={1}
              precision={3}
              value={v}
              disabled={!isEditable}
              style={{ width: '100%' }}
              onChange={(val) => updateLine(idx, { widthMm: Number(val) || null })}
            />
          ) : '—'
        },
        {
          title:     'H (mm)',
          dataIndex: 'heightMm',
          width:     90,
          align:     'right',
          render:    (v, row, idx) => isGlass(row.itemType) ? (
            <InputNumber
              size="small"
              min={0}
              step={1}
              precision={3}
              value={v}
              disabled={!isEditable}
              style={{ width: '100%' }}
              onChange={(val) => updateLine(idx, { heightMm: Number(val) || null })}
            />
          ) : '—'
        }
      );
    }

    cols.push(
      {
        title:     'Return Qty',
        dataIndex: 'returnQty',
        width:     110,
        align:     'right',
        render:    (v, row, idx) => {
          const balance = toNumber(row.returnBalanceQty);
          const overBal = balance > 0 && toNumber(v) - balance > 0.001;
          return (
            <Tooltip
              title={overBal ? `Exceeds returnable balance (${fmtNumber(balance)})` : ''}
              color={overBal ? 'red' : undefined}
            >
              <InputNumber
                size="small"
                min={0}
                step={0.001}
                precision={3}
                value={v}
                status={overBal ? 'error' : undefined}
                disabled={!isEditable}
                style={{ width: '100%' }}
                onChange={(val) => updateLine(idx, { returnQty: Number(val) || 0 })}
              />
            </Tooltip>
          );
        }
      },
      {
        title:     'UOM',
        dataIndex: 'uom',
        width:     80,
        render:    (v, _row, idx) => (
          <Input
            size="small"
            value={v || ''}
            disabled={!isEditable}
            placeholder="UOM"
            onChange={(e) => updateLine(idx, { uom: e.target.value })}
          />
        )
      }
    );

    if (anyGlass) {
      cols.push(
        {
          title:     'Return SQMT',
          dataIndex: 'returnSqmt',
          width:     110,
          align:     'right',
          render:    (v, row) => isGlass(row.itemType)
            ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNumber(v, 4)}</span>
            : '—'
        },
        {
          title:     'Return SQFT',
          dataIndex: 'returnSqft',
          width:     110,
          align:     'right',
          render:    (v, row) => isGlass(row.itemType)
            ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNumber(v, 4)}</span>
            : '—'
        }
      );
    }

    if (anyLength) {
      cols.push(
        {
          title:     'Length',
          dataIndex: 'lengthValue',
          width:     100,
          align:     'right',
          render:    (v, row, idx) => isLength(row.itemType) ? (
            <InputNumber
              size="small"
              min={0}
              step={1}
              precision={4}
              value={v}
              disabled={!isEditable}
              style={{ width: '100%' }}
              onChange={(val) => updateLine(idx, { lengthValue: Number(val) || null })}
            />
          ) : '—'
        },
        {
          title:     'Len UOM',
          dataIndex: 'lengthUom',
          width:     80,
          render:    (v, row, idx) => isLength(row.itemType) ? (
            <Input
              size="small"
              value={v || ''}
              disabled={!isEditable}
              placeholder="mm/m/ft"
              onChange={(e) => updateLine(idx, { lengthUom: e.target.value })}
            />
          ) : '—'
        },
        {
          title:     'Total Length',
          dataIndex: 'returnTotalLength',
          width:     120,
          align:     'right',
          render:    (v, row) => isLength(row.itemType)
            ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNumber(v, 4)}</span>
            : '—'
        }
      );
    }

    cols.push(
      {
        title:     'Rate',
        dataIndex: 'rate',
        width:     110,
        align:     'right',
        render:    (v, _row, idx) => (
          <InputNumber
            size="small"
            min={0}
            step={0.01}
            precision={2}
            value={v}
            disabled={!isEditable}
            style={{ width: '100%' }}
            onChange={(val) => updateLine(idx, { rate: Number(val) || 0 })}
          />
        )
      },
      {
        title:     'Gross',
        dataIndex: 'grossAmount',
        width:     110,
        align:     'right',
        render:    (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoneyAlways(v)}</span>
      },
      {
        title:     'Disc %',
        dataIndex: 'discountPercent',
        width:     85,
        align:     'right',
        render:    (v, _row, idx) => (
          <InputNumber
            size="small"
            min={0}
            max={100}
            step={0.01}
            precision={2}
            value={v}
            disabled={!isEditable}
            style={{ width: '100%' }}
            onChange={(val) => updateLine(idx, { discountPercent: Number(val) || 0 })}
          />
        )
      },
      {
        title:     'Taxable',
        dataIndex: 'taxableAmount',
        width:     120,
        align:     'right',
        render:    (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoneyAlways(v)}</span>
      },
      {
        title:     'GST %',
        dataIndex: 'gstRate',
        width:     80,
        align:     'right',
        render:    (v, _row, idx) => (
          <InputNumber
            size="small"
            min={0}
            max={28}
            step={0.01}
            precision={2}
            value={v}
            disabled={!isEditable}
            style={{ width: '100%' }}
            onChange={(val) => updateLine(idx, { gstRate: Number(val) || 0 })}
          />
        )
      },
      {
        title:     'CGST',
        dataIndex: 'cgstAmount',
        width:     100,
        align:     'right',
        render:    (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</span>
      },
      {
        title:     'SGST',
        dataIndex: 'sgstAmount',
        width:     100,
        align:     'right',
        render:    (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</span>
      },
      {
        title:     'IGST',
        dataIndex: 'igstAmount',
        width:     100,
        align:     'right',
        render:    (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</span>
      },
      {
        title:     'Line Total',
        dataIndex: 'lineTotal',
        width:     130,
        align:     'right',
        render:    (v) => (
          <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
            {fmtMoneyAlways(v)}
          </Text>
        )
      },
      {
        title:     'Returnable',
        dataIndex: 'returnBalanceQty',
        width:     110,
        align:     'right',
        render:    (v) => v
          ? <span style={{ fontVariantNumeric: 'tabular-nums', color: '#475569' }}>{fmtNumber(v)}</span>
          : <Text type="secondary">—</Text>
      },
      {
        title:     'Remarks',
        dataIndex: 'remarks',
        width:     160,
        render:    (v, _row, idx) => (
          <Input
            size="small"
            value={v || ''}
            disabled={!isEditable}
            placeholder="—"
            onChange={(e) => updateLine(idx, { remarks: e.target.value })}
          />
        )
      },
      {
        title:  '',
        width:  44,
        align:  'center',
        fixed:  'right',
        render: (_, __, idx) => (
          <Tooltip title="Remove line">
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!isEditable}
              onClick={() => removeLine(idx)}
            />
          </Tooltip>
        )
      }
    );

    return cols;
  }, [lines, isEditable, updateLine, removeLine]);


  // ── Preview blocks (Inventory / Accounting / ITC) ─────────────
  const inventoryPreviewRows = useMemo(() => {
    if (!preview) return [];
    const rows = preview.inventoryImpact
      || preview.inventory_impact
      || preview.inventory
      || [];
    return Array.isArray(rows) ? rows : [];
  }, [preview]);

  const accountingPreviewRows = useMemo(() => {
    if (!preview) return [];
    const rows = preview.accountingLines
      || preview.accounting_lines
      || preview.voucherLines
      || preview.voucher_lines
      || [];
    return Array.isArray(rows) ? rows : [];
  }, [preview]);

  const itcPreviewRows = useMemo(() => {
    if (!preview) return [];
    const rows = preview.itcReversals
      || preview.itc_reversals
      || preview.itc
      || [];
    return Array.isArray(rows) ? rows : [];
  }, [preview]);


  // ── Render ───────────────────────────────────────────────────
  const pageTitle = (() => {
    if (mode === 'new') return 'New Purchase Return';
    if (mode === 'from-inventory-ledger') return 'New Purchase Return (from GRN line)';
    if (mode === 'from-purchase-bill') return 'New Purchase Return (from Purchase Bill)';
    return 'Purchase Return';
  })();

  return (
    <div className="p-4">
      {/* Page head */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <Title level={4} style={{ marginBottom: 0 }}>
            <UndoOutlined style={{ marginInlineEnd: 8 }} />
            {pageTitle}
          </Title>
          <Text type="secondary">
            Capture the return, recompute SQMT/SQFT/length and tax in
            real-time, confirm and post to inventory + accounting +
            ITC. The backend recomputes on post and is the authority.
          </Text>
        </div>
        <Space>
          {!isCreate ? (
            <Button
              icon={<ReloadOutlined />}
              onClick={loadRecord}
              disabled={loading || saving || posting || cancelling || confirming}
            >
              Refresh
            </Button>
          ) : null}
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Space>
      </div>

      {/* Load error */}
      {loadError ? (
        <Alert
          type="error"
          showIcon
          message="Could not load return"
          description={loadError}
          style={{ marginBottom: 12 }}
          action={
            <Button size="small" onClick={loadRecord}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          disabled={!isEditable}
          requiredMark="optional"
        >
          {/* Header card */}
          <Card
            size="small"
            style={{ marginBottom: 12 }}
            title={
              <Space wrap>
                <FileTextOutlined />
                <span>Return Details</span>
                {returnNo ? (
                  <Tag color="blue">{returnNo}</Tag>
                ) : null}
                {status ? (
                  <Tag color={typeof returnStatusColor === 'function' ? returnStatusColor(status) : 'default'}>
                    {typeof returnStatusLabel === 'function' ? returnStatusLabel(status) : status}
                  </Tag>
                ) : null}
                {postingStatus ? (
                  <Tag color={typeof postingStatusColor === 'function' ? postingStatusColor(postingStatus) : 'default'}>
                    Posting: {typeof postingStatusLabel === 'function' ? postingStatusLabel(postingStatus) : postingStatus}
                  </Tag>
                ) : null}
                {voucherNo ? (
                  <Tag color="success">Voucher: {voucherNo}</Tag>
                ) : null}
              </Space>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4">
              <Form.Item label="Return No">
                <Input
                  value={returnNo || '(generated on save)'}
                  readOnly
                  disabled
                />
              </Form.Item>

              <Form.Item
                label="Return Date"
                name="returnDate"
                rules={[{ required: true, message: 'Return date is required.' }]}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>

              <Form.Item
                label="Supplier"
                name="supplierId"
                rules={[{ required: true, message: 'Supplier is required.' }]}
              >
                <Select
                  placeholder="Select supplier…"
                  showSearch
                  filterOption={false}
                  onSearch={loadSuppliers}
                  {...buildDropdownStateProps({
                    loading: supplierLoading,
                    error:   supplierError,
                    data:    supplierOptions
                  })}
                  options={supplierOptions.map((s) => ({
                    value: s.id || s.supplierId,
                    label: (
                      <span>
                        {s.supplierCode ? (
                          <code style={{ marginRight: 6, fontSize: 11, color: '#888' }}>
                            {s.supplierCode}
                          </code>
                        ) : null}
                        {s.supplierName || s.name || '(unnamed)'}
                      </span>
                    )
                  }))}
                />
              </Form.Item>

              <Form.Item label="Supplier GSTIN" name="supplierGstin">
                <Input placeholder="Auto-filled from supplier" />
              </Form.Item>

              {/* Hidden supplierName so payload builder can read it. */}
              <Form.Item name="supplierName" hidden>
                <Input />
              </Form.Item>

              <Form.Item
                label="Return Type"
                name="returnType"
                rules={[{ required: true, message: 'Return type is required.' }]}
              >
                <Select
                  options={RETURN_TYPES_LIST.map((t) => ({
                    value: t,
                    label: typeof returnTypeLabel === 'function' ? returnTypeLabel(t) : t.replace(/_/g, ' ')
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="Reason Code"
                name="reasonCode"
                rules={[{ required: true, message: 'Reason code is required.' }]}
              >
                <Select
                  options={REASON_CODES_LIST.map((r) => ({
                    value: r,
                    label: typeof reasonCodeLabel === 'function' ? reasonCodeLabel(r) : r.replace(/_/g, ' ')
                  }))}
                />
              </Form.Item>

              <Form.Item label="Source PO" name="poNo">
                <Input readOnly disabled placeholder="—" />
              </Form.Item>

              <Form.Item label="Source GRN" name="grnNo">
                <Input readOnly disabled placeholder="—" />
              </Form.Item>

              <Form.Item label="Source Purchase Bill" name="purchaseBillNo">
                <Input readOnly disabled placeholder="—" />
              </Form.Item>

              <Form.Item label="Debit Note No" name="debitNoteNo">
                <Input placeholder="Auto / manual" />
              </Form.Item>

              <Form.Item label="Debit Note Date" name="debitNoteDate">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>

              <Form.Item label="Supplier Credit Note No" name="supplierCreditNoteNo">
                <Input placeholder="Supplier reference" />
              </Form.Item>

              <Form.Item label="Supplier Credit Note Date" name="supplierCreditNoteDate">
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>

              <Form.Item label="Round Off" name="roundOff">
                <InputNumber
                  style={{ width: '100%' }}
                  step={0.01}
                  precision={2}
                  prefix="₹"
                />
              </Form.Item>
            </div>

            <Form.Item label="Remarks" name="remarks" style={{ marginBottom: 0 }}>
              <Input.TextArea rows={2} placeholder="Optional notes for this return" />
            </Form.Item>
          </Card>

          {/* Validation summary */}
          {validationErrors.length > 0 && isEditable ? (
            <Alert
              type="warning"
              showIcon
              icon={<ExclamationCircleOutlined />}
              message={`${validationErrors.length} validation issue${validationErrors.length === 1 ? '' : 's'}`}
              description={
                <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                  {validationErrors.map((e, idx) => (
                    <li key={idx}>{e}</li>
                  ))}
                </ul>
              }
              style={{ marginBottom: 12 }}
            />
          ) : null}

          {/* Lines */}
          <Card
            size="small"
            title={
              <Space>
                <span>Return Lines</span>
                <Text type="secondary">
                  ({lines.length} line{lines.length === 1 ? '' : 's'})
                </Text>
              </Space>
            }
            extra={
              <Button
                size="small"
                type="primary"
                onClick={addLine}
                disabled={!isEditable}
              >
                Add Line
              </Button>
            }
            style={{ marginBottom: 12 }}
          >
            <Table
              size="small"
              rowKey="key"
              columns={lineColumns}
              dataSource={lines}
              pagination={false}
              scroll={{ x: 1800 }}
              locale={{
                emptyText: 'No lines yet — click "Add Line" or load a draft from a GRN / Purchase Bill.'
              }}
            />
          </Card>

          {/* Summary card + preview blocks */}
          <Card size="small" style={{ marginBottom: 12 }}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Statistic
                title="Taxable Amount"
                value={fmtMoneyAlways(headerTotals.taxableAmount)}
                prefix="₹"
                valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
              />
              <Statistic
                title="CGST"
                value={fmtMoneyAlways(headerTotals.cgstAmount)}
                prefix="₹"
                valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
              />
              <Statistic
                title="SGST"
                value={fmtMoneyAlways(headerTotals.sgstAmount)}
                prefix="₹"
                valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
              />
              <Statistic
                title="IGST"
                value={fmtMoneyAlways(headerTotals.igstAmount)}
                prefix="₹"
                valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 pt-3 border-t border-slate-100">
              <Statistic
                title="Total Tax"
                value={fmtMoneyAlways(headerTotals.totalTax)}
                prefix="₹"
                valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
              />
              <Statistic
                title="Round Off"
                value={fmtMoneyAlways(headerTotals.roundOff)}
                prefix="₹"
                valueStyle={{ fontVariantNumeric: 'tabular-nums' }}
              />
              <Statistic
                title="Grand Total"
                value={fmtMoneyAlways(headerTotals.grandTotal)}
                prefix="₹"
                valueStyle={{
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums'
                }}
              />
              <div className="flex items-center">
                {validationErrors.length === 0 ? (
                  <Tag color="success" icon={<CheckCircleOutlined />}>
                    Ready for posting
                  </Tag>
                ) : (
                  <Tag color="error" icon={<CloseCircleOutlined />}>
                    Resolve issues to post
                  </Tag>
                )}
              </div>
            </div>

            {/* Collapsible preview blocks — only meaningful when a preview
                has been requested. Kept folded by default so the summary
                card stays scannable. */}
            {preview ? (
              <Collapse
                ghost
                style={{ marginTop: 16 }}
                defaultActiveKey={[]}
              >
                <Panel header={`Inventory impact (${inventoryPreviewRows.length} row${inventoryPreviewRows.length === 1 ? '' : 's'})`} key="inv">
                  {inventoryPreviewRows.length > 0 ? (
                    <Table
                      size="small"
                      rowKey={(r, i) => (r && (r.id || r.lineNo || r.itemName)) || `inv-${i}`}
                      pagination={false}
                      dataSource={inventoryPreviewRows}
                      columns={[
                        { title: 'Item',          dataIndex: 'itemName',      render: (v) => v || '—' },
                        { title: 'Movement Type', dataIndex: 'movementType',  render: (v) => v ? <Tag>{v}</Tag> : '—' },
                        { title: 'Qty',           dataIndex: 'qty',           align: 'right', render: (v) => fmtNumber(v) },
                        { title: 'UOM',           dataIndex: 'uom',           render: (v) => v || '—' }
                      ]}
                    />
                  ) : (
                    <Text type="secondary">No inventory rows in preview.</Text>
                  )}
                </Panel>

                <Panel header={`Accounting debit-note voucher (${accountingPreviewRows.length} line${accountingPreviewRows.length === 1 ? '' : 's'})`} key="acc">
                  {accountingPreviewRows.length > 0 ? (
                    <Table
                      size="small"
                      rowKey={(r, i) => (r && (r.id || r.lineNo || r.accountCode)) || `acc-${i}`}
                      pagination={false}
                      dataSource={accountingPreviewRows}
                      columns={[
                        { title: 'Account Code', dataIndex: 'accountCode', render: (v) => v ? <Text code>{v}</Text> : <Tag color="error" icon={<WarningOutlined />}>Missing</Tag> },
                        { title: 'Account Name', dataIndex: 'accountName', render: (v) => v || '—' },
                        { title: 'Debit',        dataIndex: 'debit',  align: 'right', render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</span> },
                        { title: 'Credit',       dataIndex: 'credit', align: 'right', render: (v) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</span> }
                      ]}
                    />
                  ) : (
                    <Text type="secondary">No accounting lines in preview.</Text>
                  )}
                </Panel>

                <Panel header={`ITC reversal (${itcPreviewRows.length} row${itcPreviewRows.length === 1 ? '' : 's'})`} key="itc">
                  {itcPreviewRows.length > 0 ? (
                    <Table
                      size="small"
                      rowKey={(r, i) => (r && (r.id || r.purchase_bill_line_id || r.itemName)) || `itc-${i}`}
                      pagination={false}
                      dataSource={itcPreviewRows}
                      columns={[
                        { title: 'GST %',           dataIndex: 'gstRate',         align: 'right', render: (v) => fmtNumber(v, 2) },
                        { title: 'Taxable Reversal',dataIndex: 'taxableReversal', align: 'right', render: (v) => fmtMoney(v) },
                        { title: 'CGST Reversal',   dataIndex: 'cgstReversal',    align: 'right', render: (v) => fmtMoney(v) },
                        { title: 'SGST Reversal',   dataIndex: 'sgstReversal',    align: 'right', render: (v) => fmtMoney(v) },
                        { title: 'IGST Reversal',   dataIndex: 'igstReversal',    align: 'right', render: (v) => fmtMoney(v) },
                        { title: 'Total Reversal',  dataIndex: 'totalItcReversal',align: 'right', render: (v) => <Text strong>{fmtMoneyAlways(v)}</Text> }
                      ]}
                    />
                  ) : (
                    <Text type="secondary">No ITC reversal rows in preview.</Text>
                  )}
                </Panel>
              </Collapse>
            ) : null}

            {/* Preview blockers — surface backend reasons the post would fail. */}
            {preview && Array.isArray(preview.blockers) && preview.blockers.length > 0 ? (
              <Alert
                type={preview.canPost ? 'warning' : 'error'}
                showIcon
                message={
                  preview.canPost
                    ? `${preview.blockers.length} warning${preview.blockers.length === 1 ? '' : 's'} on this preview`
                    : `Posting blocked — ${preview.blockers.length} issue${preview.blockers.length === 1 ? '' : 's'} must be resolved`
                }
                description={
                  <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                    {preview.blockers.map((b, idx) => (
                      <li key={idx}>
                        {b && b.code ? (
                          <Text code style={{ marginInlineEnd: 6 }}>{b.code}</Text>
                        ) : null}
                        <span>{(b && b.message) || 'Unknown issue.'}</span>
                      </li>
                    ))}
                  </ul>
                }
                style={{ marginTop: 12 }}
              />
            ) : null}
          </Card>
        </Form>

        {/* Post result */}
        {postResult ? (
          <Card size="small" style={{ marginBottom: 12 }}>
            <Result
              status={postResult.success ? 'success' : 'error'}
              title={
                postResult.success
                  ? `Posted as ${postResult.voucherNo || '(debit-note voucher)'}`
                  : 'Posting did not complete'
              }
              subTitle={
                <Space direction="vertical" size={4}>
                  <span>
                    Voucher No:&nbsp;<strong>{postResult.voucherNo || '—'}</strong>
                    {' · '}
                    Status:&nbsp;
                    <Tag color={typeof postingStatusColor === 'function' ? postingStatusColor(postResult.status) : 'default'}>
                      {typeof postingStatusLabel === 'function' ? postingStatusLabel(postResult.status) : postResult.status}
                    </Tag>
                  </span>
                  {postResult.postedAt ? (
                    <span>
                      Posted At:&nbsp;<strong>{fmtDateTime(postResult.postedAt)}</strong>
                    </span>
                  ) : null}
                  {postResult.errorMessage ? (
                    <Paragraph type="danger" style={{ marginBottom: 0 }}>
                      {postResult.errorMessage}
                    </Paragraph>
                  ) : null}
                </Space>
              }
              extra={[
                <Button
                  key="back"
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>,
                postResult.success ? (
                  <Button
                    key="detail"
                    type="primary"
                    onClick={() => navigate(`/erp/purchase-returns/${record.id}`)}
                  >
                    View Return
                  </Button>
                ) : (
                  <Button
                    key="retry"
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={() => { setPostResult(null); handlePost(); }}
                  >
                    Retry Post
                  </Button>
                ),
                <Button
                  key="refresh"
                  onClick={() => { setPostResult(null); setPostError(null); loadRecord(); }}
                >
                  Refresh
                </Button>
              ]}
            />
          </Card>
        ) : null}

        {/* Post error */}
        {postError && !postResult ? (
          <Alert
            type="error"
            showIcon
            message="Action failed"
            description={postError}
            style={{ marginBottom: 12 }}
            closable
            onClose={() => setPostError(null)}
          />
        ) : null}

        {/* Sticky footer toolbar */}
        {!postResult ? (
          <div
            className="flex items-center justify-end gap-2"
            style={{
              position:     'sticky',
              bottom:       0,
              background:   '#fff',
              borderTop:    '1px solid #f1f5f9',
              padding:      '10px 4px',
              marginInline: -4,
              zIndex:       2
            }}
          >
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              disabled={saving || posting || cancelling || confirming}
            >
              Back
            </Button>
            {canCancelBtn ? (
              <Button
                danger
                icon={<StopOutlined />}
                loading={cancelling}
                disabled={saving || posting || confirming}
                onClick={handleCancel}
              >
                Cancel Return
              </Button>
            ) : null}
            {isEditable ? (
              <Button
                icon={<SaveOutlined />}
                loading={saving}
                disabled={posting || cancelling || confirming}
                onClick={handleSaveDraft}
              >
                Save Draft
              </Button>
            ) : null}
            {!isCreate && record && record.id ? (
              <Button
                icon={<EyeOutlined />}
                loading={previewing}
                disabled={saving || posting || cancelling || confirming}
                onClick={handlePreview}
              >
                Preview
              </Button>
            ) : null}
            {canConfirmBtn ? (
              <Tooltip
                title={
                  validationErrors.length > 0
                    ? 'Resolve the validation issues above.'
                    : ''
                }
              >
                <Button
                  icon={<CheckCircleOutlined />}
                  loading={confirming}
                  disabled={saving || posting || cancelling || validationErrors.length > 0}
                  onClick={handleConfirm}
                >
                  Confirm
                </Button>
              </Tooltip>
            ) : null}
            {(!isCreate || record) && record && record.id ? (
              <Tooltip
                title={
                  !canPostBtn
                    ? (validationErrors.length > 0
                        ? 'Resolve the validation issues above.'
                        : !canPostPerm
                          ? 'You do not have permission to post returns.'
                          : 'This return is not ready for posting.')
                    : ''
                }
              >
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={posting}
                  disabled={!canPostBtn || saving || cancelling || confirming}
                  onClick={handlePost}
                >
                  Post Return
                </Button>
              </Tooltip>
            ) : null}
          </div>
        ) : null}
      </Spin>

      {/* Local styles */}
      <style>{`
        .pret-line-row--invalid > td { background-color: #fef2f2 !important; }
        .pret-line-row--invalid:hover > td { background-color: #fee2e2 !important; }
      `}</style>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────
// Module-level constants — keep imports tolerant so a sibling Phase
// 4 author shipping helpers with different export names doesn't
// blank out the form. RETURN_TYPES / REASON_CODES / ITEM_TYPES come
// from purchaseReturnHelpers when available, otherwise fall back to
// the canonical backend constants list.
// ─────────────────────────────────────────────────────────────────
const RETURN_TYPES_LIST = Array.isArray(RETURN_TYPES) && RETURN_TYPES.length > 0
  ? RETURN_TYPES
  : Object.freeze([
      'MATERIAL_RETURN',
      'RATE_ADJUSTMENT',
      'TAX_ADJUSTMENT',
      'QUANTITY_ADJUSTMENT'
    ]);

const REASON_CODES_LIST = Array.isArray(REASON_CODES) && REASON_CODES.length > 0
  ? REASON_CODES
  : Object.freeze([
      'DAMAGED',
      'WRONG_ITEM',
      'QUALITY_REJECTED',
      'SHORT_SUPPLY_ADJUSTMENT',
      'RATE_DIFFERENCE',
      'EXCESS_BILLING',
      'SUPPLIER_REPLACEMENT',
      'OTHER'
    ]);

const ITEM_TYPES_LIST = Array.isArray(ITEM_TYPES) && ITEM_TYPES.length > 0
  ? ITEM_TYPES
  : Object.freeze([
      'GLASS',
      'HARDWARE',
      'ACCESSORY',
      'PROFILE',
      'UPVC',
      'ALUMINIUM',
      'SERVICE',
      'JOBWORK',
      'OTHER'
    ]);

// Touch unused-but-imported identifiers so the linter doesn't complain
// when a sibling Phase author hasn't shipped them yet. The runtime
// `typeof === 'function'` guards above are the live test.
void Descriptions;
void fmtDate;
